import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n";

/**
 * Fixed viewport rail — one door shown at a time based on scroll position.
 * Prevents door overlap with content. Cat walks to right edge → E opens.
 */
const GAME_KEYS = ["runs", "fish", "yarn", "nap", "mice"];
const DOOR_W = 90;
const DOOR_H = 130;
const RAIL_RIGHT = 24;

export default function GameDoors({ catRef, onOpen }) {
  const { t } = useI18n();
  const [activeIdx, setActiveIdx] = useState(0);
  const [near, setNear] = useState(false);
  const nearRef = useRef(false);
  const activeIdxRef = useRef(0);

  // Compute active door based on scroll position (evenly split page)
  useEffect(() => {
    const compute = () => {
      const maxScroll = Math.max(1, document.body.scrollHeight - window.innerHeight);
      const p = window.scrollY / maxScroll; // 0..1
      const idx = Math.min(GAME_KEYS.length - 1, Math.floor(p * GAME_KEYS.length));
      if (idx !== activeIdxRef.current) {
        activeIdxRef.current = idx;
        setActiveIdx(idx);
      }
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, []);

  // Cat proximity to fixed door
  useEffect(() => {
    let raf;
    const loop = () => {
      const cat = catRef?.current;
      if (cat) {
        const catCenterX = cat.x + cat.w / 2;
        const catCenterY = cat.y + cat.h / 2;
        // Door center Y (fixed viewport top position)
        const doorLeft = window.innerWidth - RAIL_RIGHT - DOOR_W;
        const doorTop = window.innerHeight / 2 - DOOR_H / 2;
        const isNear =
          catCenterX >= doorLeft - 40 &&
          catCenterX <= doorLeft + DOOR_W + 40 &&
          catCenterY >= doorTop - 20 &&
          catCenterY <= doorTop + DOOR_H + 20;
        if (isNear !== nearRef.current) {
          nearRef.current = isNear;
          setNear(isNear);
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [catRef]);

  // E key opens near door
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== "KeyE") return;
      const active = document.querySelector('[data-minigame-active="true"]');
      const focused = document.activeElement;
      const inForm = focused && ["INPUT", "TEXTAREA"].includes(focused.tagName);
      if (active || inForm) return;
      if (nearRef.current) {
        e.preventDefault();
        onOpen && onOpen(GAME_KEYS[activeIdxRef.current]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpen]);

  const id = GAME_KEYS[activeIdx];

  return (
    <div
      className="fixed z-[6] pointer-events-none"
      style={{
        right: RAIL_RIGHT,
        top: "50%",
        transform: "translateY(-50%)",
        width: DOOR_W,
      }}
      data-testid="game-doors-rail"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={id}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className={`relative pointer-events-auto ${near ? "door-active" : ""}`}
          style={{ width: DOOR_W, height: DOOR_H }}
          data-testid={`game-door-${id}`}
        >
          {/* Label above — anchored to the door's right edge so it never overflows the viewport */}
          <div
            className="absolute -top-9 right-0 whitespace-nowrap font-mono-label text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border-2 border-[color:var(--ink)] shadow-[2px_2px_0_0_var(--ink)]"
            style={{ background: "var(--paper)", color: "var(--ink)" }}
          >
            {t(`door.${id}`)}
          </div>
          {/* Chapter dots */}
          <div className="absolute -left-4 top-1/2 -translate-y-1/2 flex flex-col gap-1.5">
            {GAME_KEYS.map((_, i) => (
              <span
                key={i}
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: i === activeIdx ? "var(--ink)" : "rgba(74,42,58,0.25)",
                }}
              />
            ))}
          </div>
          {/* Door */}
          <button
            onClick={() => onOpen && onOpen(id)}
            data-testid={`door-btn-${id}`}
            aria-label={t(`door.${id}`)}
            className="w-full h-full rounded-t-[42px] border-2 border-[color:var(--ink)] relative cursor-pointer"
            style={{
              background: "linear-gradient(180deg, #ff87ab 0%, #ff5d8f 100%)",
              boxShadow: "inset 0 -6px 0 rgba(0,0,0,0.2)",
            }}
          >
            <div
              className="absolute top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full border-2 border-[color:var(--ink)] flex items-center justify-center"
              style={{ background: "var(--gold)", color: "var(--ink)" }}
            >
              ★
            </div>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[color:var(--ink)]" />
            <div className="absolute top-14 left-3 right-3 h-12 rounded-md border-2 border-[color:var(--ink)] opacity-30" />
          </button>
          <AnimatePresence>
            {near && (
              <motion.div
                initial={{ y: 6, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 6, opacity: 0 }}
                className="absolute right-0 -bottom-10 whitespace-nowrap flex items-center gap-2 bg-[color:var(--paper)] border-2 border-[color:var(--ink)] rounded-full px-3 py-1 shadow-[2px_2px_0_0_var(--ink)] font-mono-label text-[10px] uppercase tracking-widest"
                data-testid={`door-prompt-${id}`}
              >
                <span className="kbd">E</span>
                <span>{t("door.press")}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
